'use client';

import { useTranslation } from 'next-i18next';

import { useMemo } from 'react';

import { Badge, Box, BoxProps, Button, ButtonProps, Skeleton, Text } from '@chakra-ui/react';

import { ScrollableWrapper } from '@nocowanie/common-ui/components/molecules/scrollable-wrapper';
import { IconsLinear } from '@nocowanie/icons/linear';

import { MAX_VISIBLE_FILTERS } from '@app/consts';
import { useActiveFilters } from '@app/hooks/useActiveFilters';
import { useMapSearch } from '@app/hooks/useMapSearch';

export const MapActiveFilters = ({
    isMapLoaded = true,
    ...rest
}: BoxProps & { isMapLoaded?: boolean }) => {
    const { t } = useTranslation('common', {
        keyPrefix: 'filters',
    });
    const { isMobileRes } = useMapSearch();
    const {
        activeFilters,
        activeFiltersCount,
        handleToggleFilters,
        handleClearAllFilters,
        isToggleLoading,
        localIsFiltersContentVisible,
        handleRemoveFilter,
    } = useActiveFilters();

    const buttonText = useMemo(() => {
        if (isMobileRes) {
            return t('filter');
        }
        return localIsFiltersContentVisible ? t('hide') : t('show');
    }, [isMobileRes, localIsFiltersContentVisible, t]);

    const toggleFiltersButtonStyles = {
        bg: !localIsFiltersContentVisible ? 'secondary.solid' : 'white',
        color: localIsFiltersContentVisible ? 'secondary.solid' : 'white',
        boxShadow: '2xl',
    };

    const ToggleFiltersButton = ({ ...props }: ButtonProps) => {
        return (
            <Skeleton asChild loading={!isMapLoaded}>
                <Button
                    onClick={handleToggleFilters}
                    size="sm"
                    borderRadius={'full'}
                    fontWeight={'normal'}
                    px={3}
                    my={{
                        base: 1,
                        md: 4,
                    }}
                    position={'relative'}
                    loading={isMobileRes && isToggleLoading}
                    disabled={isMobileRes && isToggleLoading}
                    css={isMapLoaded && toggleFiltersButtonStyles}
                    {...props}
                >
                    <IconsLinear.Settings mt={'1px'} />
                    <Text mr={1}>{buttonText}</Text>
                    {activeFiltersCount > 0 ? (
                        <Badge
                            size={'xs'}
                            borderRadius={'full'}
                            fontSize={'8px'}
                            minH={3}
                            minW={3}
                            py={0}
                            bg={'brand.solid'}
                            color={'white'}
                            fontWeight={'normal'}
                            position={'absolute'}
                            top={'3px'}
                            left={4}
                        >
                            {activeFiltersCount}
                        </Badge>
                    ) : null}
                </Button>
            </Skeleton>
        );
    };

    const commonScrollableContainerProps = {
        alignItems: 'center',
        m: 0,
        py: {
            base: 1,
            md: 4,
        },
        gap: 2,
        scrollSnapType: 'none',
        gridAutoColumns: 'max-content',
        w: 'max-content',
    };

    const renderBadges = useMemo(
        () =>
            activeFilters.slice(0, MAX_VISIBLE_FILTERS).map(filter => (
                <Skeleton asChild loading={!isMapLoaded} key={`${filter.key}-${filter.value}`}>
                    <Badge
                        variant={'chip'}
                        as={'button'}
                        onClick={() => handleRemoveFilter(filter)}
                        borderRadius={'full'}
                        px={3}
                        cursor={'pointer'}
                        boxShadow={'2xl'}
                    >
                        {filter.label}
                        <IconsLinear.Close size="sm" />
                    </Badge>
                </Skeleton>
            )),
        [activeFilters, handleRemoveFilter, isMapLoaded],
    );

    return (
        <Box display="flex" alignItems={'center'} {...rest}>
            {isMobileRes ? (
                <ScrollableWrapper
                    scrollableContainerProps={{
                        ...commonScrollableContainerProps,
                        pl: 4,
                        maxW: '100vw',
                    }}
                    loading={!isMapLoaded}
                >
                    <ToggleFiltersButton />

                    {activeFiltersCount > 0 ? (
                        <>
                            {renderBadges}

                            <Skeleton asChild loading={!isMapLoaded}>
                                <Button
                                    onClick={handleClearAllFilters}
                                    size="sm"
                                    variant={'plain'}
                                    borderRadius={'full'}
                                    color={isMapLoaded ? 'brand.solid' : undefined}
                                    bg={isMapLoaded ? 'white' : undefined}
                                    fontWeight={'normal'}
                                    boxShadow={'2xl'}
                                >
                                    {t('clear')}
                                </Button>
                            </Skeleton>
                        </>
                    ) : null}
                </ScrollableWrapper>
            ) : (
                <>
                    <ToggleFiltersButton my={4} mr={2} />
                    <ScrollableWrapper
                        scrollableContainerProps={{
                            ...commonScrollableContainerProps,
                            pl: 0,
                            maxW: '100%',
                        }}
                        showArrows
                        loading={!isMapLoaded}
                    >
                        {activeFiltersCount > 0 ? renderBadges : null}
                    </ScrollableWrapper>
                </>
            )}
        </Box>
    );
};
